'use client';

import { useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { Button } from '@/components/sections/kit';
import { routePath } from '@/lib/routes';
import { useFormGuard } from '@/components/forms/useFormGuard';
import { HUMAN_CHECK_FAILED, useHumanCheck } from '@/components/forms/useHumanCheck';

type Status = 'idle' | 'submitting' | 'success' | 'error';

/** CTA intent → pre-filled subject (e.g. /solutions/enterprise "Talk to sales"
 *  links carry ?intent=sales so the form opens ready to send). */
const INTENT_SUBJECTS: Record<string, string> = {
  sales: 'Enterprise — talk to sales',
  premium: 'Premium — please upgrade my workspace',
  demo: 'Enterprise — book a demo',
  security: 'Security review — questionnaire & DPA',
};

const inputCls =
  'w-full rounded-[11px] border border-slate-300 bg-white px-4 py-3 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none transition-colors focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20';

/**
 * Contact form wired to the existing POST /api/contact handler
 * (lib/sendgrid.ts → sendContactEmail). Fields match the handler contract:
 * name, email, message required; subject optional.
 */
export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const intent = useSearchParams().get('intent');
  const defaultSubject = intent ? INTENT_SUBJECTS[intent] : undefined;
  const { guardFields, HoneypotField } = useFormGuard();
  const human = useHumanCheck();
  const formRef = useRef<HTMLFormElement>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status === 'submitting') return;

    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: String(data.get('name') || '').trim(),
      email: String(data.get('email') || '').trim(),
      subject: String(data.get('subject') || '').trim() || undefined,
      message: String(data.get('message') || '').trim(),
      ...guardFields(),
    };

    setStatus('submitting');
    setError(null);
    try {
      // Every submission needs a fresh Turnstile token (website#302); most
      // people get one invisibly while they type.
      const turnstileToken = await human.getToken();
      if (!turnstileToken) throw new Error(HUMAN_CHECK_FAILED);
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...payload, turnstileToken }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Something went wrong. Please try again.');
      setStatus('success');
      formRef.current?.reset();
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      human.reset();
    }
  }

  if (status === 'success') {
    return (
      <div className="rounded-2xl border border-green-300 bg-green-50 px-6 py-8 text-center">
        <h3 className="text-lg font-semibold text-green-900">Message sent</h3>
        <p className="mt-2 text-sm text-green-800">
          Thanks for reaching out — we&apos;ll get back to you shortly.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-4 text-sm font-semibold text-green-900 underline underline-offset-2 hover:text-green-700"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form ref={formRef} onSubmit={handleSubmit} onFocus={human.arm} className="space-y-5" noValidate>
      <HoneypotField />
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-slate-700">
            Name
          </label>
          <input id="name" name="name" type="text" required autoComplete="name" className={inputCls} placeholder="Jane Doe" />
        </div>
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-slate-700">
            Email
          </label>
          <input id="email" name="email" type="email" required autoComplete="email" className={inputCls} placeholder="you@company.com" />
        </div>
      </div>

      <div>
        <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-slate-700">
          Subject <span className="font-normal text-slate-400">(optional)</span>
        </label>
        <input id="subject" name="subject" type="text" defaultValue={defaultSubject} className={inputCls} placeholder="How can we help?" />
      </div>

      <div>
        <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-slate-700">
          Message
        </label>
        <textarea id="message" name="message" required rows={6} className={`${inputCls} resize-y`} placeholder="Tell us a bit about what you need…" />
      </div>

      {status === 'error' && (
        <p role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </p>
      )}

      {human.widget}

      <Button
        type="submit"
        disabled={status === 'submitting'}
        className={`px-6 py-3.5 text-base ${status === 'submitting' ? 'opacity-60' : ''}`}
      >
        {status === 'submitting' ? 'Sending…' : 'Send message'}
      </Button>

      <p className="text-xs leading-[1.55] text-slate-500">
        Your name, email and message are used only to handle your enquiry,
        delivered through SendGrid, our email provider, and kept only as long
        as follow-up questions can be expected. Details in the{' '}
        <a href={`${routePath('legalPrivacy')}#contact-requests`} className="underline underline-offset-2 hover:text-brand-600">
          Privacy Policy
        </a>
        .
      </p>
    </form>
  );
}
